// src/api/adminHttp.js
import API_BASE_URL from "./baseUrl";
import {
  clearAdminSession,
  getAdminToken,
  hasAdminSession,
} from "../admin/adminAuth";

const BASE = API_BASE_URL;

function buildHeaders(initHeaders, token, contentType) {
  return {
    ...(contentType ? { "Content-Type": contentType } : {}),
    ...(initHeaders || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseBody(res) {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(data, status) {
  if (!data) return `Request failed (${status})`;
  if (typeof data === "string") return data || `Request failed (${status})`;

  return (
    data.error ||
    data.message ||
    data.detail ||
    data.title ||
    `Request failed (${status})`
  );
}

function handleUnauthorized(res, requestPath) {
  if (res.status !== 401) return;

  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const isAdminAuthRoute =
    String(requestPath || "").startsWith("/admin/auth/") ||
    path.startsWith("/admin/login");

  if (!isAdminAuthRoute && hasAdminSession()) {
    clearAdminSession();

    if (typeof window !== "undefined") {
      const next = encodeURIComponent(path || "/admin");
      window.location.replace(`/admin/login?next=${next}`);
    }
  }
}

/**
 * Low-level admin fetch wrapper.
 */
export async function adminApi(path, init = {}) {
  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const token = getAdminToken();

  let body = init.body;
  const headers = { ...(init.headers || {}) };

  if (init.json !== undefined) {
    body = JSON.stringify(init.json);
    delete init.json;

    if (!headers["Content-Type"] && !headers["content-type"]) {
      headers["Content-Type"] = "application/json";
    }
  }

  const contentType =
    headers["Content-Type"] || headers["content-type"] || undefined;

  const finalHeaders = buildHeaders(headers, token, contentType);

  const res = await fetch(url, {
    ...init,
    body,
    headers: finalHeaders,
    credentials: "omit",
  });

  handleUnauthorized(res, path);
  return res;
}

export async function adminJson(path, init = {}) {
  const res = await adminApi(path, {
    ...init,
    headers: {
      ...(init.headers || {}),
      "Content-Type": (init.headers || {})["Content-Type"] || "application/json",
    },
  });

  const data = await parseBody(res);

  if (!res.ok) {
    const err = new Error(extractErrorMessage(data, res.status));
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export async function adminText(path, textBody = "", init = {}) {
  const res = await adminApi(path, {
    method: "POST",
    ...init,
    body: textBody ?? "",
    headers: {
      "Content-Type": "text/plain",
      ...(init.headers || {}),
    },
  });

  const data = await parseBody(res);

  if (!res.ok) {
    const err = new Error(extractErrorMessage(data, res.status));
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}